import { pickableExpenseCategories } from '@console/shared-kernel';
import { ChevronLeft, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { FormFooter } from '@/components/ui/form-footer';
import { prefillReceipt, type ReceiptPrefill } from '@/lib/prefill-receipt';
import { useActiveContextValue } from '@/lib/active-context-store';
import { cn } from '@/lib/utils';
import { getReport } from '../reports/api';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import { createExpense, getExpense, updateExpense, uploadDocument, type KnownProblem } from './api';
import { MAX_DOCUMENT_BYTES } from './document';
import { DocumentPane, type DocumentChoice, type DocumentStage } from './DocumentPane';
import { ExpenseFields } from './ExpenseFields';
import { LeaveDialog } from './LeaveDialog';
import { OwnerField } from './OwnerField';
import { PrefillNotice, RecoveryNotice } from './PrefillNotice';
import { ReportField } from './ReportField';
import { SaveErrorNotice } from './SaveErrorNotice';
import { useFormRecovery } from './use-form-recovery';
import {
  applyProposals,
  checkTotals,
  EMPTY_FORM,
  formFromExpense,
  formKind,
  missingFields,
  proposalsFromPrefill,
  toExpenseInput,
  type Expense,
  type ExpenseForm,
  type ExpenseInput,
  type Proposals,
} from './form-model';
import type { NoteType } from '../reports/types';
import { useAllowedCategories } from '../reports/use-allowed-categories';
import { useOnboardingClick } from '@/communications/use-onboarding-click';

// What the prefill may propose: never a server-managed category, such as the allowance.
const CATEGORY_CODES: ReadonlySet<string> = new Set(pickableExpenseCategories().map((c) => c.code));

type Stage = DocumentStage | 'fields';
type SaveError = KnownProblem | 'OTHER' | 'documentFailed';
const UPLOAD_PROBLEMS: ReadonlySet<KnownProblem | 'OTHER'> = new Set([
  'NOT_A_PDF',
  'DOCUMENT_TOO_LARGE',
  'VERSION_CONFLICT',
]);
type Reading = 'idle' | 'reading' | 'failed';

function choiceFromExpense(expense: Expense): DocumentChoice {
  switch (expense.documentStatus) {
    case 'attached':
      return { kind: 'existing', pages: expense.document?.pages ?? 0 };
    case 'awaited':
      return { kind: 'awaited', note: expense.expectedDocumentNote ?? '' };
    case 'not-required':
      return { kind: 'not-required' };
    default:
      return { kind: 'unset' };
  }
}

/** What the request says about the document, beside the fields. */
function documentFields(choice: DocumentChoice): Partial<ExpenseInput> {
  if (choice.kind === 'awaited') {
    return { documentStatus: 'awaited', expectedDocumentNote: choice.note };
  }
  if (choice.kind === 'not-required') {
    return { documentStatus: 'not-required', expectedDocumentNote: null };
  }
  // A PDF follows with PUT …/document, or the stored one stays.
  return {};
}

/** The form without the owner's own choices: their card and vehicle. */
const withoutOwnerChoices = (current: ExpenseForm): ExpenseForm => ({
  ...current,
  cardId: '',
  cardLabel: '',
  vehicleId: '',
  vehicleLabel: '',
});

type NoteOfOrigin =
  | { readonly status: 'none' | 'loading' | 'failed' }
  | { readonly status: 'ready'; readonly ownerUserId: string; readonly periodMonth: string };

/**
 * The note «Aggiungi spesa» opened the form from (`?reportId=`, #946): its owner, for Gestione's
 * «Per conto di», and its month, for «Nota» until a date is typed. Not read when editing.
 */
function useNoteOfOrigin(reportId: string, editing: boolean): NoteOfOrigin {
  const [origin, setOrigin] = useState<NoteOfOrigin>(() =>
    reportId === '' || editing ? { status: 'none' } : { status: 'loading' },
  );
  useEffect(() => {
    if (reportId === '' || editing) return;
    let live = true;
    void getReport(reportId).then((outcome) => {
      if (!live) return;
      setOrigin(
        outcome.ok
          ? {
              status: 'ready',
              ownerUserId: outcome.value.owner.userId,
              periodMonth: outcome.value.periodMonth,
            }
          : { status: 'failed' },
      );
    });
    return () => {
      live = false;
    };
  }, [reportId, editing]);
  return origin;
}

/** The page's state, and the load of the expense being edited. */
function useCaptureState(expenseId: string | undefined) {
  const editing = expenseId !== undefined;

  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'failed'>(
    editing ? 'loading' : 'ready',
  );
  const [expense, setExpense] = useState<Expense | null>(null);
  const [form, setForm] = useState<ExpenseForm>(EMPTY_FORM);
  const [proposals, setProposals] = useState<Proposals>({});
  /** The form and proposals as loaded: what «unchanged» means, and where a reset goes back to. */
  const [loaded, setLoaded] = useState<{ form: ExpenseForm; proposals: Proposals }>({
    form: EMPTY_FORM,
    proposals: {},
  });
  const [choice, setChoice] = useState<DocumentChoice>({ kind: 'unset' });
  const [stage, setStage] = useState<Stage>(editing ? 'fields' : 'document');
  /** «Per conto di»: the chosen owner's id, `''` for the caller. */
  const [ownerUserId, setOwnerUserId] = useState('');
  const permissions = useExpensesPermissions();
  const manages = holds(permissions, EXPENSES_MANAGE);
  /** «Nota»: the report the new expense is filed in; «Nuova spesa» from a report preselects it. */
  const [initialReportId] = useState(useSearchParams()[0].get('reportId') ?? '');
  const [reportId, setReportId] = useState(initialReportId);
  const selfUserId = useAuth().user?.uid;
  const origin = useNoteOfOrigin(initialReportId, editing);
  /** The chosen note's type (wave 2, R-a): it decides which categories the form offers. */
  const [reportNoteType, setReportNoteType] = useState<NoteType | null>(null);
  const pickReport = useCallback((id: string, noteType: NoteType | null = null) => {
    setReportId(id);
    setReportNoteType(id === '' ? null : noteType);
  }, []);

  const { load } = useLoadExpense(expenseId, setLoadState, (next) => {
    const nextForm = formFromExpense(next);
    const nextProposals = proposalsFromPrefill(
      next.prefill as unknown as ReceiptPrefill | null,
      CATEGORY_CODES,
    );
    setExpense(next);
    setLoaded({ form: nextForm, proposals: nextProposals });
    setForm(nextForm);
    setProposals(nextProposals);
    setChoice(choiceFromExpense(next));
  });

  const permissionsReady = permissions.status === 'ready';
  // #946: opened from someone else's note, Gestione files for that note's owner. Settled once the
  // note and the permissions are known; until then «Nota» keeps the note it was opened from.
  const noteOwnerUserId =
    origin.status === 'ready' && manages && origin.ownerUserId !== selfUserId
      ? origin.ownerUserId
      : '';
  const [originSettled, setOriginSettled] = useState(origin.status === 'none');
  useEffect(() => {
    if (originSettled || !permissionsReady || origin.status === 'loading') return;
    if (origin.status === 'failed') pickReport('');
    if (noteOwnerUserId !== '')
      setOwnerUserId((current) => (current === '' ? noteOwnerUserId : current));
    setOriginSettled(true);
  }, [originSettled, permissionsReady, origin.status, noteOwnerUserId, pickReport]);

  // An owner kept from when the caller could choose one, and no longer can: the caller's own.
  useEffect(() => {
    if (permissionsReady && !manages && ownerUserId !== '') {
      setOwnerUserId('');
      // The other owner's card and vehicle are not the caller's.
      setForm(withoutOwnerChoices);
    }
  }, [permissionsReady, manages, ownerUserId]);

  return {
    editing,
    loadState,
    expense,
    setExpense,
    loaded,
    form,
    setForm,
    proposals,
    setProposals,
    choice,
    setChoice,
    stage,
    setStage,
    ownerUserId,
    setOwnerUserId,
    manages,
    initialReportId,
    reportId,
    setReportId: pickReport,
    selfUserId,
    noteOwnerUserId,
    originMonth: origin.status === 'ready' ? origin.periodMonth : undefined,
    originSettled,
    reportNoteType,
    load,
  };
}

function DocumentColumn({
  stage,
  choice,
  onChoice,
  onStage,
  replaceable,
}: {
  readonly stage: Stage;
  readonly choice: DocumentChoice;
  readonly onChoice: (next: DocumentChoice) => void;
  readonly onStage: (stage: Stage) => void;
  readonly replaceable: boolean;
}) {
  return (
    <div
      className={cn(
        `min-w-0 rounded-xl border border-border bg-card p-4 min-[900px]:sticky
        min-[900px]:top-4 min-[900px]:max-h-[calc(100dvh-2rem)] min-[900px]:overflow-y-auto`,
        stage === 'fields' && 'hidden min-[900px]:block',
      )}
    >
      <DocumentPane
        choice={choice}
        onChoice={onChoice}
        onStage={onStage}
        onContinue={() => onStage('fields')}
        replaceable={replaceable}
      />
    </div>
  );
}

/**
 * «Nuova spesa» and its edit mode (design §1.3, §1.4; the approved board, decision 132): the
 * document, then the fields the receipt prefill proposes, then «Salva bozza». On a phone the steps
 * follow one another; on a computer the document sits on the left and the fields on the right.
 * Saving creates the draft (or PATCHes it with If-Match), then uploads the PDF, then opens the
 * expense.
 */
export function CaptureExpensePage() {
  // The email's button lands here with `?onboarding=<step>`: logged once, then removed (#739).
  useOnboardingClick();
  // Everything on the page belongs to the active organization: its people, draft reports, cards,
  // vehicles, suppliers and the chosen document. Another organization starts the page afresh, and
  // the form recovery reads that organization's own copy.
  const context = useActiveContextValue();
  const organization = context === null ? '' : `${context.tenantId}:${context.organizationId}`;
  return <CaptureExpenseForm key={organization} />;
}

function CaptureExpenseForm() {
  const { expenseId } = useParams();
  const state = useCaptureState(expenseId);
  const { editing, loadState, expense, setExpense, form, setForm, proposals, setProposals } = state;
  const { choice, setChoice, stage, setStage, ownerUserId, setOwnerUserId, manages } = state;
  const { initialReportId, reportId, setReportId, reportNoteType, load, loaded } = state;
  const { selfUserId, noteOwnerUserId, originMonth, originSettled } = state;

  const { reading, prefill, setPrefill, readDocument } = useReading(setProposals, setForm);
  const { t } = useTranslation('expenses');

  const target = useMemo(
    () => ({ ownerUserId, reportId, reportNoteType }),
    [ownerUserId, reportId, reportNoteType],
  );
  const baseline = useMemo(
    () => ({
      form: loaded.form,
      target: { ownerUserId: noteOwnerUserId, reportId: initialReportId, reportNoteType: null },
    }),
    [loaded.form, initialReportId, noteOwnerUserId],
  );
  const recovery = useFormRecovery({
    draftId: expenseId ?? 'new',
    form,
    proposals,
    prefill,
    target,
    baseline,
    ready: loadState === 'ready',
    // The owner and the report are checked again by their fields against the active context.
    onRestore: (kept) => {
      setForm(kept.form);
      setProposals(kept.proposals);
      setPrefill(kept.prefill);
      setOwnerUserId(kept.target.ownerUserId);
      setReportId(kept.target.reportId, kept.target.reportNoteType);
    },
    onReset: () => {
      setForm(loaded.form);
      setProposals(loaded.proposals);
      setPrefill(null);
      setChoice(expense ? choiceFromExpense(expense) : { kind: 'unset' });
      setOwnerUserId(noteOwnerUserId);
      setReportId(initialReportId);
    },
  });
  // A chosen PDF is unsaved too, though the local copy cannot keep it.
  const leave = useLeaveGuard(recovery.dirty || choice.kind === 'pdf');

  const { saving, saveError, setSaveError, canSave, save } = useSave({
    editing,
    expense,
    form,
    choice,
    ownerUserId,
    reportId,
    manages,
    prefill,
    load,
    setExpense,
    onSaved: () => {
      leave.allow();
      recovery.clear();
    },
  });

  const onChoice = (next: DocumentChoice): void => {
    setChoice(next);
    setStage('fields');
    setSaveError(null);
    if (next.kind === 'pdf') void readDocument(next.pdf);
  };

  /**
   * The form's changes; choosing a mileage type with no document chosen yet answers the document
   * step with «Non serve» (decision 145).
   */
  const onFormChange = (next: ExpenseForm): void => {
    setForm(next);
    if (formKind(next) === 'mileage' && formKind(form) !== 'mileage' && choice.kind === 'unset') {
      setChoice({ kind: 'not-required' });
    }
  };

  const closeTo = editing ? `/expenses/${expenseId}` : '/expenses';
  const title = editing ? t('capture.editTitle') : t('capture.title');

  const notice = <CaptureNotice loadState={loadState} title={title} closeTo={closeTo} />;
  if (loadState !== 'ready' || (expense && !expense.can.edit)) return notice;

  const onOwnerChange = (userId: string): void => {
    setOwnerUserId(userId);
    // Cards and vehicles are the owner's: another owner, another list.
    setForm(withoutOwnerChoices);
    if (saveError === 'OWNER_NOT_IN_ORGANIZATION') setSaveError(null);
  };

  return (
    <div className="space-y-4">
      {leave.asking ? <LeaveDialog onStay={leave.stay} onLeave={leave.proceed} /> : null}
      <CaptureHead title={title} closeTo={closeTo} />
      {/* Above the steps: on the phone the «Dati» step may be the hidden one (U10). */}
      {recovery.restored && <RecoveryNotice editing={editing} onDismiss={recovery.dismissNotice} />}

      <StepList stage={stage} />

      <div className="grid gap-6 min-[900px]:items-start min-[900px]:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <DocumentColumn
          stage={stage}
          choice={choice}
          onChoice={onChoice}
          onStage={setStage}
          replaceable={editing}
        />

        <FieldsColumn
          hidden={stage !== 'fields'}
          reading={reading}
          form={form}
          proposals={proposals}
          onFormChange={onFormChange}
          editing={editing}
          expense={expense}
          ownerUserId={ownerUserId}
          manages={manages}
          onOwnerChange={onOwnerChange}
          reportId={reportId}
          onReportChange={setReportId}
          selfUserId={selfUserId}
          originMonth={originMonth}
          originWaiting={!originSettled}
          noteType={reportNoteType}
          saveError={saveError}
          closeTo={closeTo}
          choice={choice}
          canSave={canSave}
          saving={saving}
          onBack={() => setStage('document')}
          onSave={() => void save()}
        />
      </div>
    </div>
  );
}

type FieldsColumnProps = {
  readonly hidden: boolean;
  readonly reading: Reading;
  readonly form: ExpenseForm;
  readonly proposals: Proposals;
  readonly onFormChange: (next: ExpenseForm) => void;
  readonly editing: boolean;
  readonly expense: Expense | null;
  readonly ownerUserId: string;
  readonly manages: boolean;
  readonly onOwnerChange: (userId: string) => void;
  readonly reportId: string;
  readonly onReportChange: (reportId: string, noteType?: NoteType | null) => void;
  readonly selfUserId: string | undefined;
  readonly originMonth: string | undefined;
  readonly originWaiting: boolean;
  readonly noteType: NoteType | null;
  readonly saveError: SaveError | null;
  readonly closeTo: string;
  readonly choice: DocumentChoice;
  readonly canSave: boolean;
  readonly saving: boolean;
  readonly onBack: () => void;
  readonly onSave: () => void;
};

function FieldsColumn({
  hidden,
  reading,
  form,
  proposals,
  onFormChange,
  editing,
  expense,
  ownerUserId,
  manages,
  onOwnerChange,
  reportId,
  onReportChange,
  selfUserId,
  originMonth,
  originWaiting,
  noteType,
  saveError,
  closeTo,
  choice,
  canSave,
  saving,
  onBack,
  onSave,
}: FieldsColumnProps) {
  const { t } = useTranslation('expenses');
  const allowed = useAllowedCategories(noteType);
  // A category the chosen note's type does not accept is cleared, so the form never holds it.
  useEffect(() => {
    if (allowed && form.category !== '' && !allowed.has(form.category)) {
      onFormChange({ ...form, category: '' });
    }
  }, [allowed, form, onFormChange]);
  return (
    <div
      className={cn(
        'min-w-0 space-y-4 rounded-xl border border-border bg-card p-4',
        hidden && 'hidden min-[900px]:block',
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="min-h-11 min-[900px]:hidden"
        onClick={onBack}
      >
        <ChevronLeft aria-hidden="true" />
        {t('capture.steps.document')}
      </Button>
      <PrefillNotice reading={reading} />

      <ExpenseFields
        form={form}
        proposals={proposals}
        onChange={onFormChange}
        listOwner={editing ? expense?.owner.userId : ownerUserId || undefined}
        allowedCategories={allowed}
        owner={
          manages ? (
            <OwnerField
              value={ownerUserId}
              onChange={onOwnerChange}
              existingOwner={
                editing ? (expense?.owner.displayName ?? t('capture.owner.unknown')) : undefined
              }
            />
          ) : undefined
        }
      />

      {!editing && (
        <ReportField
          date={form.date}
          ownerUserId={ownerUserId}
          manages={manages}
          value={reportId}
          onChange={onReportChange}
          selfUserId={selfUserId}
          fallbackMonth={originMonth}
          waiting={originWaiting}
        />
      )}

      {saveError && <SaveErrorNotice error={saveError} />}

      <SaveBar
        closeTo={closeTo}
        form={form}
        documentUnset={choice.kind === 'unset'}
        canSave={canSave}
        saving={saving}
        failed={saveError !== null}
        onSave={onSave}
      />
    </div>
  );
}

/** Loads the expense being edited, now and again after a version conflict. */
function useLoadExpense(
  expenseId: string | undefined,
  setLoadState: (state: 'loading' | 'ready' | 'failed') => void,
  onLoaded: (expense: Expense) => void,
) {
  const adopt = useRef(onLoaded);
  adopt.current = onLoaded;
  const load = useCallback(async (): Promise<void> => {
    if (expenseId === undefined) return;
    const outcome = await getExpense(expenseId);
    if (outcome.ok) {
      adopt.current(outcome.value);
      setLoadState('ready');
    } else {
      setLoadState('failed');
    }
  }, [expenseId, setLoadState]);

  useEffect(() => {
    void load();
  }, [load]);
  return { load };
}

function CaptureHead({ title, closeTo }: { readonly title: string; readonly closeTo: string }) {
  const { t } = useTranslation('expenses');
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h1 className="screen-title">{title}</h1>
        <p className="screen-summary hidden min-[900px]:block">{t('capture.summary')}</p>
      </div>
      <Button asChild variant="ghost" size="icon" aria-label={t('capture.close')}>
        <Link to={closeTo}>
          <X />
        </Link>
      </Button>
    </div>
  );
}

function CaptureNotice({
  loadState,
  title,
  closeTo,
}: {
  readonly loadState: 'loading' | 'ready' | 'failed';
  readonly title: string;
  readonly closeTo: string;
}) {
  const { t } = useTranslation('expenses');
  if (loadState === 'ready') {
    return (
      <section className="space-y-2">
        <h1 className="screen-title">{title}</h1>
        <p className="text-sm">{t('capture.notEditable')}</p>
        <Link to={closeTo} className="text-sm underline">
          {t('capture.close')}
        </Link>
      </section>
    );
  }
  return (
    <section className="space-y-2">
      <h1 className="screen-title">{title}</h1>
      {loadState === 'loading' ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t('capture.loading')}
        </p>
      ) : (
        <p role="alert" className="text-sm text-destructive">
          {t('capture.loadFailed')}
        </p>
      )}
    </section>
  );
}

const STEPS: readonly Stage[] = ['document', 'pages', 'fields'];

function StepList({ stage }: { readonly stage: Stage }) {
  const { t } = useTranslation('expenses');
  return (
    <ol aria-label={t('capture.steps.label')} className="flex gap-2 text-sm min-[900px]:hidden">
      {STEPS.map((step, index) => (
        <li
          key={step}
          aria-current={stage === step ? 'step' : undefined}
          className={cn(
            'rounded-full px-3 py-1',
            stage === step
              ? 'bg-selected font-semibold text-selected-foreground'
              : 'text-muted-foreground',
          )}
        >
          {index + 1} {t(`capture.steps.${step}`)}
        </li>
      ))}
    </ol>
  );
}

function SaveBar({
  closeTo,
  form,
  documentUnset,
  canSave,
  saving,
  failed,
  onSave,
}: {
  readonly closeTo: string;
  readonly form: ExpenseForm;
  readonly documentUnset: boolean;
  readonly canSave: boolean;
  readonly saving: boolean;
  readonly failed: boolean;
  readonly onSave: () => void;
}) {
  const { t } = useTranslation('expenses');
  const missing = missingFields(form);
  let reason: string | null = null;
  if (missing.length > 0) {
    reason = t('capture.save.missing', {
      fields: missing
        .map((key) => t(`capture.fields.${key as 'date'}`).replace(/\?$/, ''))
        .join(', '),
    });
  } else if (documentUnset) {
    reason = t('capture.save.chooseDocument');
  }
  let label = t('capture.save.button');
  if (saving) label = t('capture.save.saving');
  else if (failed) label = t('capture.save.retry');
  return (
    <FormFooter
      sticky="page"
      reason={reason}
      primaryLabel={label}
      primaryDisabled={!canSave}
      onPrimary={onSave}
      cancelLabel={t('capture.save.cancel')}
      cancelTo={closeTo}
    />
  );
}

/**
 * Leaving with unsaved changes asks first, whatever the way out: «Chiudi», «Annulla», the browser's
 * Back, a link of the shell (decision 182, U10). Confirmed, the navigation goes on to where it was
 * headed; the local copy stays either way. Closing or reloading the tab gets the browser's own
 * question. `allow()` lets the next navigation through, for the one after a save.
 */
function useLeaveGuard(dirty: boolean) {
  const allowed = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !allowed.current && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // Older browsers ask only when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  return {
    asking: blocker.state === 'blocked',
    stay: () => blocker.reset?.(),
    proceed: () => blocker.proceed?.(),
    allow: () => {
      allowed.current = true;
    },
  };
}

/** Reads the receipt of a chosen PDF; only the latest run counts. */
function useReading(
  setProposals: (proposals: Proposals) => void,
  setForm: (update: (current: ExpenseForm) => ExpenseForm) => void,
) {
  const [reading, setReading] = useState<Reading>('idle');
  /** The prefill response as received in this session, sent with the save. */
  const [prefill, setPrefill] = useState<ReceiptPrefill | null>(null);
  const readingRun = useRef(0);
  const readDocument = async (pdf: Blob): Promise<void> => {
    const run = ++readingRun.current;
    setReading('reading');
    const outcome = await prefillReceipt(pdf);
    if (run !== readingRun.current) return;
    if (outcome.kind === 'ok') {
      setPrefill(outcome.prefill);
      const next = proposalsFromPrefill(outcome.prefill, CATEGORY_CODES);
      setProposals(next);
      setForm((current) => applyProposals(current, next));
      setReading(outcome.prefill.readable ? 'idle' : 'failed');
    } else {
      // A 403 or any other refusal simply means no proposal; a service that is down says so.
      setReading(outcome.kind === 'unavailable' ? 'failed' : 'idle');
    }
  };
  return { reading, prefill, setPrefill, readDocument };
}

type SaveArgs = {
  readonly editing: boolean;
  readonly expense: Expense | null;
  readonly form: ExpenseForm;
  readonly choice: DocumentChoice;
  readonly ownerUserId: string;
  readonly reportId: string;
  readonly manages: boolean;
  readonly prefill: ReceiptPrefill | null;
  readonly load: () => Promise<void>;
  readonly setExpense: (expense: Expense) => void;
  readonly onSaved: () => void;
};

function requestBody(a: SaveArgs): ExpenseInput {
  return {
    ...toExpenseInput(a.form),
    ...documentFields(a.choice),
    ...(!a.editing && a.manages && a.ownerUserId !== '' ? { ownerUserId: a.ownerUserId } : {}),
    ...(!a.editing && a.reportId !== '' ? { reportId: a.reportId } : {}),
    ...(a.prefill ? { prefill: a.prefill as unknown as NonNullable<ExpenseInput['prefill']> } : {}),
  };
}

/** The save: create or PATCH the draft, then upload the PDF, then open the expense. */
function useSave(a: SaveArgs) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  /** The draft POST created when the upload after it failed: a retry only uploads. */
  const [createdId, setCreatedId] = useState<string | null>(null);
  const valid =
    missingFields(a.form).length === 0 &&
    checkTotals(a.form).kind === 'ok' &&
    a.choice.kind !== 'unset';
  const canSave = valid && !saving;

  const upload = async (id: string): Promise<boolean> => {
    const choice = a.choice;
    if (choice.kind !== 'pdf') return true;
    if (choice.pdf.size > MAX_DOCUMENT_BYTES) {
      setSaveError('DOCUMENT_TOO_LARGE');
      return false;
    }
    const outcome = await uploadDocument(id, choice.pdf, choice.source);
    if (outcome.ok) return true;
    // The draft exists by now: only the upload's own problems are named, the rest is «documento
    // non caricato» (a refusal by status, #946, would read as if the expense were not saved).
    setSaveError(UPLOAD_PROBLEMS.has(outcome.code) ? outcome.code : 'documentFailed');
    return false;
  };

  /** The expense's id once the draft exists on the server; null when the write was refused. */
  const persist = async (body: ExpenseInput): Promise<string | null> => {
    if (a.editing && a.expense) {
      const outcome = await updateExpense(a.expense.id, a.expense.version, body);
      if (!outcome.ok) {
        setSaveError(outcome.code);
        if (outcome.code === 'VERSION_CONFLICT') await a.load();
        return null;
      }
      a.setExpense(outcome.value);
      return outcome.value.id;
    }
    if (createdId !== null) return createdId;
    const outcome = await createExpense(body);
    if (!outcome.ok) {
      setSaveError(outcome.code);
      return null;
    }
    return outcome.value.id;
  };

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const id = await persist(requestBody(a));
      if (id === null) return;
      if (!(await upload(id))) {
        if (!a.editing) setCreatedId(id);
        return;
      }
      a.onSaved();
      void navigate(`/expenses/${id}`);
    } finally {
      setSaving(false);
    }
  };
  return { saving, saveError, setSaveError, canSave, save };
}
